import React, { useState, useMemo } from 'react';
import type { JSX } from 'react';
import { useGetMessageListQuery, Message } from '@/store/api/Message List/messageLIstApi';
import Loader from '@/components/Loader';
import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Users, Search } from 'lucide-react';
import { cn } from '@/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useNewDebounce } from '@/hooks/useNewDebounce';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
}

function StatCard({ label, value, icon: Icon }: StatCardProps) {
  return (
    <div
      className="
        w-[250px] min-w-[250px] max-w-[250px]
        h-[100px]
        bg-white
        inner-shadow-blue
        rounded-xl
        px-6 py-5
        transition-all
        duration-500
        hover:bg-[linear-gradient(to_right,#082261,#34065c)]
        hover:text-white group"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-5 h-5 opacity-80 group-hover:text-white" />
      </div>
    </div>
  );
}

export default function MessageTable(): JSX.Element {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useNewDebounce(search, 500);

  const { data, isLoading, isFetching, isError } = useGetMessageListQuery({
    skip: pageIndex * pageSize,
    take: pageSize,
    type: 'Wishlist',
  });

  const columns = useMemo<ColumnDef<Message>[]>(
    () => [
      {
        id: 'sno',
        header: 'S. No.',
        cell: ({ row, table }) => {
          const { pageIndex: pIndex, pageSize: pSize } = (table.options.meta as any)
            ?.pagination ?? {
            pageIndex: 0,
            pageSize: 10,
          };
          return pIndex * pSize + row.index + 1;
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => row.original.name?.trim() || '-',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => row.original.email?.trim() || '-',
      },
      // {
      //   accessorKey: 'mailSent',
      //   header: 'Mail Sent',
      //   cell: ({ row }) => (
      //     <span
      //       className={cn(
      //         'px-2 py-1 rounded-full text-xs font-medium',
      //         row.original.mailSent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      //       )}
      //     >
      //       {row.original.mailSent ? 'Yes' : 'No'}
      //     </span>
      //   ),
      // },
      // {
      //   accessorKey: 'mailSentAt',
      //   header: 'Mail Sent At',
      //   cell: ({ row }) =>
      //     row.original.mailSentAt
      //       ? new Date(row.original.mailSentAt).toLocaleString('en-IN', {
      //         day: '2-digit',
      //         month: 'short',
      //         year: 'numeric',
      //         hour: '2-digit',
      //         minute: '2-digit',
      //       })
      //       : '-',
      // },
      {
        accessorKey: 'createdAt',
        header: 'Registered Date',
        cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
      },
    ],
    []
  );

  if (isError) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center space-y-4">
        <p className="text-red-600 font-semibold text-lg">Failed to load waiting list</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#082261] text-white rounded-lg hover:bg-[#082261]/90 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  const messages = useMemo(() => {
    const list = data?.data ?? [];
    if (!debouncedSearch) return list;
    const q = debouncedSearch.toLowerCase();
    return list.filter(
      (m) => m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    );
  }, [data, debouncedSearch]);

  const totalCount = data?.count ?? 0;

  return (
    <div className="relative space-y-6">
      {(isLoading || isFetching) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/5 backdrop-blur-sm">
          <Loader />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-spectral">Waiting List</h1>
          <p className="text-muted-foreground">Manage and view users on the waiting list.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-6 mt-4">
        <StatCard label="Total Entries" value={isLoading ? '—' : totalCount} icon={Users} />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2 relative max-w-[360px]">
            <Label htmlFor="search">Search Waitlist</Label>
            <Search
              size={20}
              className="absolute -translate-y-1/2 right-3 top-[60%] text-muted-foreground"
            />
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="
                  w-full
                  border border-[#001B6B]
                  rounded-lg px-3 py-2 pr-10
                  outline-none
                  focus:ring-0
                "
            />
          </div>
        </CardContent>
      </Card>

      <div className="w-full font-lato">
        <DataTable
          data={messages}
          columns={columns as any}
          enableSearch={false}
          enableFilters={false}
          enableColumnVisibility={false}
          manualPagination={!debouncedSearch}
          pageSize={pageSize}
          pageCount={Math.ceil(totalCount / pageSize)}
          onPaginationChange={({ pageIndex: pIndex, pageSize: pSize }) => {
            setPageIndex(pIndex);
            setPageSize(pSize);
          }}
          isLoading={isLoading || isFetching}
        />
      </div>
    </div>
  );
}
